import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { livePhotoImage, photographyVideoImage } from '@images/imageUrls';
import PhotographyMediaIcon from '@components/photographyMediaIcon';
import styles from './index.module.scss';

interface Props {
    src: string;
    poster?: string;
    title?: string;
    width?: number;
    height?: number;
    active?: boolean;
    paused?: boolean;
    autoPlay?: boolean;
    /** 作品列表可能不包含视频地址，需要等待详情接口补全。 */
    sourceLoading?: boolean;
    /** 详情页将细进度条放在作者信息下方；播放器仍负责进度和拖动状态。 */
    progressHost?: React.RefObject<HTMLDivElement>;
    /** 外层作品浏览器在横屏播放期间暂停翻页，并分层处理返回。 */
    onExpandedChange?: (expanded: boolean) => void;
}
const formatTime = (value: number) => {
    const seconds = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

/** 复用作品弹窗；所有操作均为自定义控件，横屏只展开当前播放器。 */
const PhotographyVideoPlayer: React.FC<Props> = ({ src, poster, title, width, height, active = true, paused = false, autoPlay = true, sourceLoading = false, progressHost, onExpandedChange }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inlineHostRef = useRef<HTMLDivElement>(null);
    const [playerHost, setPlayerHost] = useState<HTMLDivElement | null>(null);
    const resumeTime = useRef(0);
    const resumePlaying = useRef(autoPlay);
    const sourceRef = useRef(src);
    const gesture = useRef<{ playing: boolean } | null>(null);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(!autoPlay);
    const [interaction, setInteraction] = useState(0);
    const [seeking, setSeeking] = useState(false);
    const [waiting, setWaiting] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [ratio, setRatio] = useState(Number(width) > 0 && Number(height) > 0 ? Number(width) / Number(height) : 0);
    const [failed, setFailed] = useState(false);
    const [progressTarget, setProgressTarget] = useState<HTMLDivElement | null>(null);
    const [inlineSize, setInlineSize] = useState({ width: 750, height: 0 });
    const landscape = ratio > 1;
    const unit = inlineSize.width / 750;
    const enabled = !!src && !failed && active && !paused;
    const showControls = controlsVisible || seeking || (!expanded && !playing);
    const showToolbar = showControls && (autoPlay || expanded || playing || currentTime > 0);

    useEffect(() => { onExpandedChange?.(expanded); }, [expanded, onExpandedChange]);

    const revealControls = () => { setControlsVisible(true); setInteraction(value => value + 1); };
    const startPlayback = useCallback((video: HTMLVideoElement) => {
        resumePlaying.current = true;
        video.play().catch(() => {
            if (video !== videoRef.current || !video.paused) return;
            resumePlaying.current = false;
            setPlaying(false); setWaiting(false); setControlsVisible(true);
        });
    }, []);
    useLayoutEffect(() => {
        const host = document.createElement('div');
        host.className = styles.host;
        inlineHostRef.current?.appendChild(host);
        setPlayerHost(host);
        return () => host.remove();
    }, []);
    useLayoutEffect(() => {
        const destination = expanded ? document.body : inlineHostRef.current;
        if (!playerHost || !destination || playerHost.parentNode === destination) return;
        const video = videoRef.current;
        const wasPlaying = video && !video.paused && !video.ended;
        // 保持同一个视频节点和播放进度；切换 portal 容器会重建视频并丢失有声播放授权。
        destination.appendChild(playerHost);
        if (wasPlaying) startPlayback(video);
    }, [expanded, playerHost, startPlayback]);
    // 覆盖弹窗、切换作品和组件卸载时，移除原生视频层并释放资源。
    const attachVideo = useCallback((node: HTMLVideoElement | null) => {
        const previous = videoRef.current;
        if (previous && previous !== node) {
            resumeTime.current = previous.currentTime;
            previous.pause();
            previous.removeAttribute('src');
            previous.load();
        }
        videoRef.current = node;
    }, []);

    useLayoutEffect(() => {
        if (sourceRef.current !== src) {
            sourceRef.current = src;
            resumeTime.current = 0;
            resumePlaying.current = autoPlay;
            gesture.current = null;
            setCurrentTime(0); setDuration(0); setFailed(false); setPlaying(false);
            setSeeking(false); setWaiting(false); setControlsVisible(!autoPlay); setExpanded(false);
        }
    }, [src, autoPlay]);
    // WebView 不一定会执行动态挂载视频的 autoplay 属性，进入作品或切换作品时主动启动静音播放。
    useEffect(() => {
        if (playerHost && src && active && !paused && autoPlay && resumePlaying.current && videoRef.current) {
            startPlayback(videoRef.current);
        }
    }, [playerHost, src, active, paused, autoPlay, startPlayback]);
    // 宿主在播放器之后挂载，等待整棵树的 ref 就绪再放入进度条。
    useEffect(() => { setProgressTarget(progressHost?.current || null); }, [progressHost]);
    useEffect(() => {
        if (Number(width) > 0 && Number(height) > 0) setRatio(Number(width) / Number(height));
    }, [width, height]);
    useLayoutEffect(() => {
        if (expanded || !containerRef.current) return;
        const node = containerRef.current;
        const measure = () => {
            const rect = node.getBoundingClientRect();
            if (rect.width) setInlineSize({ width: rect.width, height: rect.height });
        };
        measure();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
        observer?.observe(node);
        window.addEventListener('resize', measure);
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
    }, [expanded, playerHost]);
    useEffect(() => {
        if (!active || paused) {
            resumePlaying.current = false;
            gesture.current = null;
            videoRef.current?.pause();
            setPlaying(false); setSeeking(false); setWaiting(false); setControlsVisible(true); setExpanded(false);
        }
    }, [active, paused]);
    useEffect(() => {
        const pauseOnHide = () => {
            if (document.hidden) { resumePlaying.current = false; videoRef.current?.pause(); }
        };
        document.addEventListener('visibilitychange', pauseOnHide);
        return () => document.removeEventListener('visibilitychange', pauseOnHide);
    }, []);
    useEffect(() => {
        if (!playing || !controlsVisible || seeking) return;
        const timer = window.setTimeout(() => setControlsVisible(false), 3000);
        return () => window.clearTimeout(timer);
    }, [playing, controlsVisible, seeking, interaction]);

    const changeExpanded = useCallback((next: boolean) => {
        gesture.current = null;
        setSeeking(false); setExpanded(next); setControlsVisible(!next);
    }, []);
    useEffect(() => {
        if (!expanded) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') changeExpanded(false); };
        document.addEventListener('keydown', onKeyDown);
        return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', onKeyDown); };
    }, [expanded, changeExpanded]);
    const play = (restart = true) => {
        const video = videoRef.current;
        if (!video || !enabled) return;
        if (restart && (video.ended || (duration > 0 && video.currentTime >= duration))) video.currentTime = 0;
        startPlayback(video);
    };
    const togglePlaying = () => {
        revealControls();
        if (!videoRef.current || !enabled) return;
        if (!videoRef.current.paused && !videoRef.current.ended) {
            resumePlaying.current = false;
            videoRef.current.pause();
        } else {
            // 详情页没有音量工具栏；用户主动播放时开启声音，自动播放仍保持静音。
            if (progressHost) setMuted(false);
            play();
        }
    };
    const finishSeek = () => {
        const previous = gesture.current;
        if (!previous) return;
        gesture.current = null;
        setSeeking(false); revealControls();
        if (previous.playing && enabled) play(false);
    };
    const progress = (compact = false) => <div className={classNames(styles.progress, compact && styles.compactProgress)}
        style={{ '--played': `${duration ? Math.min(100, currentTime / duration * 100) : 0}%`, ...(compact ? { fontSize: `${unit}px` } : {}) } as React.CSSProperties}>
        <input type="range" aria-label="播放进度" aria-valuetext={`${formatTime(currentTime)} / ${formatTime(duration)}`}
            min={0} max={duration || 0} step={0.1} value={Math.min(currentTime, duration || 0)} disabled={!enabled || !duration}
            onClick={event => event.stopPropagation()}
            onPointerDown={event => {
                if (!enabled || !duration) return;
                event.stopPropagation();
                gesture.current = { playing: !!videoRef.current && !videoRef.current.paused && !videoRef.current.ended };
                event.currentTarget.setPointerCapture?.(event.pointerId);
                videoRef.current?.pause(); setSeeking(true); revealControls();
            }}
            onChange={event => {
                const time = Number(event.target.value);
                if (videoRef.current) videoRef.current.currentTime = time;
                resumeTime.current = time; setCurrentTime(time); revealControls();
            }}
            onPointerUp={finishSeek} onPointerCancel={finishSeek} onLostPointerCapture={finishSeek} onBlur={finishSeek}
        />
    </div>;
    const mediaHeight = Math.max(0, inlineSize.height - (landscape && !progressHost ? 88 * unit : 0));
    const promptTop = (mediaHeight + Math.min(mediaHeight, ratio ? inlineSize.width / ratio : mediaHeight)) / 2 + 24 * unit;
    const player = <div ref={containerRef} className={classNames(styles.player, progressHost && styles.detailPlayer, expanded && styles.expanded, landscape && styles.landscape)}
        style={{ '--player-unit': `${unit}px` } as React.CSSProperties} onClick={event => event.stopPropagation()}>
        <div className={styles.media} tabIndex={enabled ? 0 : -1} aria-label="视频画面，按空格键播放或暂停"
            onKeyDown={event => {
                if (event.target === event.currentTarget && (event.key === ' ' || event.key === 'Enter')) {
                    event.preventDefault(); togglePlaying();
                }
            }} onClick={() => {
            if (!expanded && playing && progressHost) togglePlaying();
            else setControlsVisible(value => !value);
        }}>
            {src && active && !paused ? <video key={src} ref={attachVideo} src={src} poster={poster} aria-label={title || '作品视频'}
                playsInline disablePictureInPicture webkit-playsinline="true" x5-playsinline="true" x5-video-player-type="h5" x5-video-player-fullscreen="false"
                controls={false} preload="metadata" autoPlay={resumePlaying.current} muted={muted}
                onLoadedMetadata={event => {
                    const video = event.currentTarget;
                    const total = Number.isFinite(video.duration) ? video.duration : 0;
                    setDuration(total);
                    if (video.videoWidth && video.videoHeight) setRatio(video.videoWidth / video.videoHeight);
                    if (resumeTime.current > 0) video.currentTime = Math.min(resumeTime.current, total || resumeTime.current);
                    setCurrentTime(video.currentTime);
                }}
                onDurationChange={event => setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0)}
                onTimeUpdate={event => { if (!gesture.current) setCurrentTime(event.currentTarget.currentTime); }}
                onPlay={event => {
                    if (event.currentTarget !== videoRef.current) return;
                    setPlaying(true); setFailed(false);
                }}
                onPlaying={event => {
                    if (event.currentTarget !== videoRef.current) return;
                    setPlaying(true); setFailed(false); setWaiting(false);
                }}
                onPause={event => {
                    if (event.currentTarget === videoRef.current && event.currentTarget.paused) { setPlaying(false); setWaiting(false); }
                }} onWaiting={() => setWaiting(true)} onCanPlay={() => setWaiting(false)}
                onSeeked={() => setWaiting(false)}
                onEnded={() => { resumePlaying.current = false; setPlaying(false); setWaiting(false); setControlsVisible(true); }}
                onError={event => {
                    const video = event.currentTarget;
                    // 释放旧视频、取消加载和没有 MediaError 的事件不能判定当前作品播放失败。
                    if (video !== videoRef.current || !video.getAttribute('src') || !video.error || video.error.code === 1) return;
                    setFailed(true); setPlaying(false); setWaiting(false);
                }} onContextMenu={event => event.preventDefault()}
            /> : poster && <img src={poster} alt={title || '视频封面'} />}
            {!sourceLoading && (!src || failed) && <span className={styles.unavailable}>视频暂不可播放</span>}
            {((sourceLoading && (!src || failed)) || (waiting && playing)) && <span className={styles.loading} role="status" aria-label="视频加载中" />}
            {!expanded && enabled && !playing && <button type="button" className={styles.centerPlay}
                aria-label="播放视频" onClick={event => { event.stopPropagation(); togglePlaying(); }}>
                <PhotographyMediaIcon kind="play" disc />
            </button>}
        </div>
        {!expanded && landscape && enabled && <button type="button" className={styles.fullscreenPrompt} style={{ top: `${promptTop}px` }} onClick={() => changeExpanded(true)}>
            <img src={photographyVideoImage.fullscreen} alt="" />全屏观看
        </button>}
        {expanded && <div className={classNames(styles.topShade, !showControls && styles.controlsHidden)} aria-hidden={!showControls}>
            <button type="button" className={styles.back} aria-label="退出全屏" onClick={() => changeExpanded(false)} tabIndex={showControls ? 0 : -1}>
                <img src={photographyVideoImage.back} alt="" />
            </button>
        </div>}
        {enabled && (expanded || !progressHost) && <div className={classNames(styles.controls, !showToolbar && styles.controlsHidden)} aria-hidden={!showToolbar}>
            <div className={styles.time}>{formatTime(currentTime)}<span> / </span><span>{formatTime(duration)}</span></div>
            {(expanded || !progressTarget) && progress()}
            <div className={styles.controlButtons}>
                <button type="button" aria-label={playing ? '暂停' : '继续播放'} onClick={togglePlaying} tabIndex={showControls ? 0 : -1}>
                    <PhotographyMediaIcon kind={playing ? 'pause' : 'play'} />
                </button>
                {!expanded && <button type="button" aria-label={muted ? '开启声音' : '静音'} onClick={() => { setMuted(value => !value); revealControls(); }} tabIndex={showControls ? 0 : -1}>
                    <img src={muted ? livePhotoImage.volumeClose : livePhotoImage.volume} alt="" />
                </button>}
                {!expanded && <button type="button" aria-label="全屏观看" onClick={() => changeExpanded(true)} tabIndex={showControls ? 0 : -1}>
                    <img src={photographyVideoImage.fullscreen} alt="" />
                </button>}
            </div>
        </div>}
    </div>;
    return <><div ref={inlineHostRef} className={styles.host} />
        {playerHost && createPortal(player, playerHost)}
        {!expanded && progressTarget && createPortal(progress(true), progressTarget)}
    </>;
};
export default PhotographyVideoPlayer;
